<template>
  <main class="single-film-prototype single-person-prototype single-film-list-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="defaultBackgroundImage"
        background-wrap-class="film-list-hero__bgWrap"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end film-list-hero__grid">
          <div class="col hero__meta film-list-hero__meta">
            <div class="upper-container">
              <div class="user-event-container film-list-hero__meta-stack">
                <input
                  v-if="listEditMode"
                  v-model="editableListTitle"
                  class="item-title film-list-detail__title-input"
                  data-testid="film-list-title-input"
                  type="text"
                >
                <h1 v-else class="item-title" data-testid="film-list-title">
                  {{ filmList.title || '' }}
                </h1>
                <p
                  v-if="filmListLoaded && !filmListError"
                  class="film-list-hero__meta-line"
                  data-testid="film-list-meta"
                >
                  {{ filmCountLabel }}
                  <span aria-hidden="true"> • </span>
                  <CuratedListLabel v-if="filmList.is_curated" tag="span" />
                  <template v-else>
                    <span>List by </span>
                    <a data-testid="film-list-owner-link" :href="filmListOwnerPath">{{ filmList.owner_name || 'Unknown' }}</a>
                  </template>
                  <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
                  <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
                </p>
                <div v-if="listFavouritesEnabled && !listEditMode" class="buttons film-list-hero__buttons">
                  <template v-if="!isAuthenticated">
                    <a class="btn single-btn person-fav-btn" data-testid="film-list-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                  </template>
                  <template v-else>
                    <button
                      class="btn single-btn person-fav-btn film-list-hero__favourite-button"
                      data-testid="film-list-favourite-action"
                      type="button"
                      title="List favourites are not available yet."
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="mobile g-0 film-list-mobile-meta container">
      <div class="row g-0 item-title-mobile">
        <input
          v-if="listEditMode"
          v-model="editableListTitle"
          class="film-list-detail__title-input film-list-detail__title-input--mobile"
          data-testid="film-list-title-input-mobile"
          type="text"
        >
        <h1 v-else data-testid="film-list-title-mobile">{{ filmList.title || '' }}</h1>
      </div>
      <div v-if="filmListLoaded && !filmListError" class="row g-0">
        <p class="film-list-hero__meta-line film-list-hero__meta-line--mobile">
          {{ filmCountLabel }}
          <span aria-hidden="true"> • </span>
          <CuratedListLabel v-if="filmList.is_curated" tag="span" />
          <template v-else>
            <span>List by </span>
            <a data-testid="film-list-owner-link-mobile" :href="filmListOwnerPath">{{ filmList.owner_name || 'Unknown' }}</a>
          </template>
          <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
          <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
        </p>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div v-if="listFavouritesEnabled && !listEditMode" class="buttons">
            <template v-if="!isAuthenticated">
              <a class="btn single-btn" data-testid="film-list-favourite-action-mobile" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
            </template>
            <template v-else>
              <button
                class="btn single-btn film-list-hero__favourite-button"
                data-testid="film-list-favourite-action-mobile"
                type="button"
                title="List favourites are not available yet."
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'overview' }"
                  :to="overviewPath"
                  data-testid="film-list-overview-tab"
                >
                  Overview
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="commentsPath"
                  data-testid="film-list-comments-tab"
                >
                  Comments
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div
                      v-if="listEditMode || filmList.description || filmListLoading || filmListError"
                      class="col g-0 bio"
                    >
                      <template v-if="filmListLoaded && !filmListError && listEditMode">
                        <p
                          class="film-list-detail__description-label"
                          data-testid="film-list-description-label"
                        >
                          Overview
                        </p>
                        <textarea
                          v-model="editableListDescription"
                          class="film-list-page__description film-list-detail__description-textarea"
                          data-testid="film-list-description-textarea"
                          rows="5"
                        />
                      </template>
                      <p
                        v-else-if="filmListLoaded && !filmListError && filmList.description"
                        class="film-list-page__description"
                        data-testid="film-list-description"
                      >
                        {{ filmList.description }}
                      </p>
                      <p v-else-if="filmListLoading" class="film-lists-empty" data-testid="film-list-loading">
                        Loading list...
                      </p>
                      <p v-else-if="filmListError" class="film-lists-empty" data-testid="film-list-error">
                        {{ filmListError }}
                      </p>
                    </div>
                  </div>
                </div>

                <template v-if="filmListLoaded && !filmListError">
                  <div
                    ref="listContentStartRef"
                    class="row g-0 sorting-buttons-row"
                    :class="{ 'sorting-buttons-row--list-editing': listEditMode }"
                  >
                    <div class="col g-0">
                      <div class="sorting-buttons">
                        <span class="sort-title">Sort:</span>
                        <div class="sorting-buttons__primary">
                          <button
                            v-for="button in primarySortButtons"
                            :key="button.key"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            type="button"
                            @click="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </button>
                        </div>
                        <div v-if="secondarySortButtons.length" class="sorting-buttons__secondary">
                          <button
                            v-for="button in secondarySortButtons"
                            :key="button.key"
                            :class="[
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            type="button"
                            @click="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </button>
                        </div>
                        <div class="sorting-buttons__desktop-secondary-row">
                          <div class="sorting-buttons__title">
                            <button
                              v-for="button in titleSortButtons"
                              :key="button.key"
                              :class="[
                                'sorting-btn',
                                button.active ? 'active' : null,
                              ]"
                              type="button"
                              @click="setSortMode(button.key)"
                            >
                              {{ button.label }}
                            </button>
                          </div>
                          <button
                            v-for="button in viewModeButtons"
                            :key="`desktop-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            data-testid="film-list-desktop-view-mode-button"
                            :aria-label="button.label"
                            :title="button.label"
                            type="button"
                            @click="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" data-testid="film-list-view-mode-icon" />
                          </button>
                        </div>
                        <div class="view-mode-btns">
                          <button
                            v-for="button in viewModeButtons"
                            :key="`mobile-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            :aria-label="button.label"
                            :title="button.label"
                            type="button"
                            @click="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" />
                          </button>
                          <button
                            class="sorting-btn filter-btn view-mode-filter-btn"
                            data-testid="film-list-mobile-filter-button"
                            type="button"
                            @click="toggleFilters"
                          >
                            {{ filterButtonLabel }}
                          </button>
                        </div>
                      </div>

                      <div
                        class="modal"
                        data-testid="film-list-filter-modal"
                        :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                        @click.self="toggleFilters"
                      >
                        <button
                          class="person-compact-rating-picker__close person-filter-modal__close"
                          data-testid="film-list-filter-modal-close"
                          type="button"
                          aria-label="Close filters"
                          @click="toggleFilters"
                        >
                          <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                        </button>
                        <div class="container person-filters">
                          <div class="person-filter-modal__header">
                            <h2>Filters:</h2>
                            <button
                              class="sorting-btn person-filter-modal__apply person-filter-modal__apply-mobile"
                              data-testid="film-list-filter-modal-apply"
                              type="button"
                              @click="toggleFilters"
                            >
                              Apply
                            </button>
                          </div>
                          <div
                            v-for="row in filterModalRows"
                            :key="row.key"
                            class="row g-0 filters-groups-row person-filter-modal__row"
                          >
                            <div
                              v-for="group in row.groups"
                              :key="group.key"
                              class="person-filter-modal__group person-filter-modal__group--half col-6"
                              data-testid="film-list-filter-modal-group"
                            >
                              <div class="single-filter-title-row">
                                <h3>{{ group.title }}</h3>
                              </div>
                              <div class="filter-row">
                                <a
                                  v-for="option in group.options"
                                  :key="option.value"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="!reorderMode" class="row g-0 filters-section-sm">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div class="container g-0">
                          <div class="row g-0">
                            <div class="col-9 g-0 filtering-row">
                              <input
                                v-model="titleFilter"
                                class="filmography-filter"
                                data-testid="film-list-filter-input"
                                type="search"
                                placeholder="Filter by title or release year..."
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="filmListCanEdit"
                    class="row g-0 list-addition-row film-list-detail__edit-row"
                  >
                    <div class="col g-0">
                      <button
                        v-if="!reorderMode"
                        class="btn-sm list-addition-btn"
                        data-testid="film-list-edit-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startListEditing"
                      >
                        {{ listEditMode ? 'Stop editing' : 'Edit list' }}
                      </button>
                      <label
                        v-if="listEditMode"
                        class="film-list-detail__public-edit"
                        data-testid="film-list-public-edit-field"
                      >
                        <input
                          v-model="editableListPublic"
                          data-testid="film-list-public-edit"
                          type="checkbox"
                        >
                        <span>Public</span>
                      </label>
                      <button
                        v-if="!listEditMode && !reorderMode"
                        class="btn-sm"
                        data-testid="film-list-reorder-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startReordering"
                      >
                        Reorder items
                      </button>
                      <ResetListNumbers
                        v-if="!listEditMode && !reorderMode"
                        kind="film"
                        :pending="resetNumbersPending"
                        :notice="resetNumbersNotice"
                        @reset="resetNumbers"
                      />
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="film-list-reorder-done-button"
                        type="button"
                        @click="finishReordering"
                      >
                        Done reordering
                      </button>
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="film-list-reorder-save-button"
                        type="button"
                        @click="saveReordering"
                      >
                        Save new order
                      </button>
                      <button
                        v-if="reorderMode && hasReorderChanges"
                        class="btn-sm film-list-detail__discard-button"
                        data-testid="film-list-reorder-discard-button"
                        type="button"
                        @click="discardReorderChanges"
                      >
                        Discard changes
                      </button>
                      <p
                        v-if="reorderMode"
                        class="film-list-detail__reorder-instructions"
                        data-testid="film-list-reorder-instructions"
                      >
                        Use the arrow buttons to move items up or down.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__edit-actions">
                        <button
                          class="btn-sm"
                          data-testid="film-list-save-button"
                          type="button"
                          @click="saveListEditing"
                        >
                          Save
                        </button>
                        <button
                          v-if="hasUnsavedChanges"
                          class="btn-sm film-list-detail__discard-button"
                          data-testid="film-list-discard-button"
                          type="button"
                          @click="discardListEditing"
                        >
                          Discard changes
                        </button>
                      </div>
                      <p
                        v-if="showUnsavedChangesNotice"
                        class="film-list-detail__edit-warning"
                        data-testid="film-list-unsaved-notice"
                      >
                        You have unsaved changes. Save or discard the changes.
                      </p>
                      <p
                        v-if="showSavedChangesNotice"
                        class="film-list-detail__edit-success"
                        data-testid="film-list-saved-notice"
                        @click="dismissSavedChangesNotice"
                      >
                        Changes saved.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__add-item-panel">
                        <p class="film-list-detail__add-item-title" data-testid="film-list-add-item-title">
                          Add new list item
                        </p>
                        <div class="top-search-autocomplete film-list-detail__add-item-search">
                          <div class="top-search-autocomplete__field">
                            <input
                              v-model="addItemQuery"
                              class="top-search form-control"
                              data-testid="film-list-add-item-input"
                              type="search"
                              placeholder="Search films..."
                              @input="handleAddItemSearchInput"
                              @keydown.down.prevent="highlightNextAddItemResult"
                              @keydown.up.prevent="highlightPreviousAddItemResult"
                              @keydown.enter.prevent="handleAddItemEnter"
                              @keydown.esc="resetAddItemSearch"
                            >
                            <div
                              v-if="addItemSearchResults.length > 0"
                              class="top-search-autocomplete__results"
                              data-testid="film-list-add-item-results"
                            >
                              <button
                                v-for="(result, index) in addItemSearchResults"
                                :key="`film-list-add-item-${result.id}`"
                                :ref="(element) => setAddItemResultRef(element, index)"
                                :class="[
                                  'top-search-autocomplete__result',
                                  'film-list-detail__add-item-result',
                                  {
                                    'top-search-autocomplete__result--active': index === addItemActiveIndex,
                                    'top-search-autocomplete__result--disabled': isFilmAddItemDisabled(result.id),
                                  },
                                ]"
                                :disabled="isFilmAddItemDisabled(result.id)"
                                data-testid="film-list-add-item-result"
                                type="button"
                                @mouseenter="setSelectableAddItemActiveIndex(result, index)"
                                @focus="setSelectableAddItemActiveIndex(result, index)"
                                @click="addSearchResult(result)"
                              >
                                <span class="top-search-autocomplete__label">
                                  <span class="top-search-autocomplete__keyword">{{ result.title }}</span>
                                  <span
                                    v-if="isFilmAlreadyOnList(result.id)"
                                    class="top-search-autocomplete__year"
                                  >
                                    (already on the list)
                                  </span>
                                  <span
                                    v-if="result.year"
                                    class="top-search-autocomplete__year"
                                  >
                                    ({{ result.year }})
                                  </span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <p
                          v-if="addItemNotice"
                          class="film-list-detail__edit-success"
                          data-testid="film-list-add-item-notice"
                          @click="addItemNotice = ''"
                        >
                          {{ addItemNotice }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="container g-0">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div
                          v-if="reorderMode"
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list film-list-detail__reorder-list"
                          data-testid="film-list-reorder-list"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="film-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <template v-else>
                            <p
                              v-if="reorderMoveNotice.position === 'above'"
                              class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--above"
                              data-testid="film-list-reorder-notice-above"
                            >
                              {{ reorderMoveNotice.text }}
                            </p>
                            <div
                              v-for="(item, index) in reorderPaginatedItems"
                              :key="item.id"
                              :class="[
                                'row',
                                'g-0',
                                'person-text-only-credit-row',
                                'film-list-detail__text-row',
                                index === reorderPaginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                              ]"
                              data-testid="film-list-reorder-row"
                            >
                              <div class="col g-0">
                                <div class="person-text-only-row film-list-detail__text-row-top">
                                  <span
                                    class="film-list-detail__text-number film-list-detail__reorder-number"
                                    data-testid="film-list-reorder-number"
                                  >
                                    {{ reorderDisplayNumber(index) }}
                                  </span>
                                  <div class="film-list-detail__reorder-position-actions">
                                    <span
                                      v-if="isFirstReorderItem(item.id)"
                                      class="film-list-detail__reorder-position-placeholder"
                                      aria-hidden="true"
                                    ></span>
                                    <button
                                      v-else
                                      class="btn-sm film-list-detail__reorder-position-button"
                                      data-testid="film-list-reorder-move-up"
                                      type="button"
                                      title="Move item up"
                                      aria-label="Move item up"
                                      @click="moveReorderItem(item.id, -1)"
                                    >
                                      ↑
                                    </button>
                                    <button
                                      v-if="!isLastReorderItem(item.id)"
                                      class="btn-sm film-list-detail__reorder-position-button"
                                      data-testid="film-list-reorder-move-down"
                                      type="button"
                                      title="Move item down"
                                      aria-label="Move item down"
                                      @click="moveReorderItem(item.id, 1)"
                                    >
                                      ↓
                                    </button>
                                  </div>
                                  <a
                                    :href="item.film.url || '#'"
                                    class="film-list-detail__text-title-link"
                                    data-testid="film-list-text-title-link"
                                  >
                                    <h3 class="person-text-only-credit-film-title">
                                      {{ truncatedFilmTitle(item.film.title) }}
                                    </h3>
                                  </a>
                                  <span v-if="item.film.year" class="person-text-only-release-year">
                                    <span class="person-text-only-parenthesis">(</span>{{ item.film.year }}<span class="person-text-only-parenthesis">)</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p
                              v-if="reorderMoveNotice.position === 'below'"
                              class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--below"
                              data-testid="film-list-reorder-notice-below"
                            >
                              {{ reorderMoveNotice.text }}
                            </p>
                          </template>
                        </div>

                        <div
                          v-else-if="currentViewMode === '1'"
                          class="container g-0 credits-list film-list-detail__credits-list"
                          data-testid="film-list-full-view"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="film-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <template v-for="(item, index) in paginatedItems" :key="item.id">
                            <div
                              v-if="listEditMode && isListItemPendingRemoval(item.id)"
                              class="row g-0 film-list-detail__removed-placeholder-wrap"
                              data-testid="film-list-item-removed-placeholder-wrap"
                            >
                              <div class="col g-0">
                                <div class="film-list-detail__removed-placeholder" data-testid="film-list-item-removed-placeholder">
                                  <span class="film-list-detail__removed-placeholder-text">
                                    '{{ item.film.title }}' is to be deleted on save.
                                  </span>
                                  <button
                                    class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                    data-testid="film-list-item-undo-remove-button"
                                    type="button"
                                    @click="undoListItemRemoval(item.id)"
                                  >
                                    Undo
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!(listEditMode && isListItemPendingRemoval(item.id))"
                              class="row g-0 single-credit-row film-list-detail__row"
                              data-testid="film-list-item-row"
                            >
                              <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                <p class="film-list-detail__number" data-testid="film-list-item-number">
                                  {{ item.number || '–' }}
                                </p>
                                <div class="film-poster-container">
                                  <a :href="item.film.url || '#'">
                                    <img
                                      class="film-poster"
                                      :src="item.film.poster_url || posterPlaceholderImage"
                                      :alt="`${item.film.title} poster`"
                                    >
                                  </a>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="container g-0 single-credit-container film-list-detail__container">
                                  <div class="row g-0 credit-title-row">
                                    <div class="col g-0">
                                      <a :href="item.film.url || '#'">
                                        <h3 class="credit-film-title">
                                          {{ truncatedFilmTitle(item.film.title) }}
                                          <span v-if="item.film.year" class="release-year">({{ item.film.year }})</span>
                                        </h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div
                                    v-if="!listEditMode"
                                    class="film-list-detail__mobile-controls"
                                    data-testid="film-list-item-mobile-controls"
                                  >
                                    <div class="row g-0 film-list-detail__mobile-buttons-row">
                                      <div class="col g-0">
                                        <div class="person-buttons person-buttons--credit" data-testid="film-list-item-action-group-xs">
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn left-btn"
                                              :class="{ 'btn--watched': item.film.watched }"
                                              type="button"
                                              :disabled="filmWatchPendingBySlug[item.film.slug] || item.film.watched"
                                              @click="markListItemWatched(item)"
                                            >
                                              <div class="default-btn">
                                                <i :class="listItemWatchIconClass(item)"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn left-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-plus"></i>
                                              </div>
                                            </a>
                                          </template>
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn middle-btn"
                                              :class="{ 'btn--watchlisted': item.film.watchlisted }"
                                              type="button"
                                              :disabled="filmWatchlistPendingBySlug[item.film.slug]"
                                              @click="toggleListItemWatchlist(item)"
                                            >
                                              <div class="default-btn">
                                                <i class="ion-bookmark"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn middle-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-bookmark"></i>
                                              </div>
                                            </a>
                                          </template>
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn right-btn"
                                              :class="{ 'btn--favourited': item.film.favourited }"
                                              type="button"
                                              :disabled="filmFavouritePendingBySlug[item.film.slug]"
                                              @click="toggleListItemFavourite(item)"
                                            >
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn right-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </a>
                                          </template>
                                        </div>
                                      </div>
                                    </div>
                                    <div class="row g-0 film-list-detail__mobile-rating-row">
                                      <div class="col g-0">
                                        <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile-stack">
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="person-credit-rating-summary"
                                              type="button"
                                              :disabled="filmRatingPendingBySlug[item.film.slug]"
                                              @click="openRatingPicker(item)"
                                            >
                                              <i :class="listItemRatingStarClass(item)"></i>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="person-credit-rating-summary" :href="loginPath">
                                              <i class="rating-star ion-ios-star-outline"></i>
                                            </a>
                                          </template>
                                          <div class="current-credits-rating">
                                            <p>{{ listItemRatingText(item) }}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div v-if="!listEditMode" class="row g-0 film-list-detail__actions-row">
                                    <div class="col g-0">
                                      <div class="person-buttons person-buttons--credit" data-testid="film-list-item-action-group">
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn left-btn"
                                            :class="{ 'btn--watched': item.film.watched }"
                                            type="button"
                                            :disabled="filmWatchPendingBySlug[item.film.slug] || item.film.watched"
                                            @click="markListItemWatched(item)"
                                          >
                                            <div class="default-btn">
                                              <i :class="listItemWatchIconClass(item)"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn left-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-plus"></i>
                                            </div>
                                          </a>
                                        </template>
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn middle-btn"
                                            :class="{ 'btn--watchlisted': item.film.watchlisted }"
                                            type="button"
                                            :disabled="filmWatchlistPendingBySlug[item.film.slug]"
                                            @click="toggleListItemWatchlist(item)"
                                          >
                                            <div class="default-btn">
                                              <i class="ion-bookmark"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn middle-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-bookmark"></i>
                                            </div>
                                          </a>
                                        </template>
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn right-btn"
                                            :class="{ 'btn--favourited': item.film.favourited }"
                                            type="button"
                                            :disabled="filmFavouritePendingBySlug[item.film.slug]"
                                            @click="toggleListItemFavourite(item)"
                                          >
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn right-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </a>
                                        </template>
                                      </div>
                                    </div>
                                    <div class="col g-0">
                                      <div class="rating-stars film-list-detail__rating">
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="person-credit-rating-summary"
                                            type="button"
                                            :disabled="filmRatingPendingBySlug[item.film.slug]"
                                            @click="openRatingPicker(item)"
                                          >
                                            <i :class="listItemRatingStarClass(item)"></i>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="person-credit-rating-summary" :href="loginPath">
                                            <i class="rating-star ion-ios-star-outline"></i>
                                          </a>
                                        </template>
                                        <div class="current-credits-rating">
                                          <p>{{ listItemRatingText(item) }}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    v-if="listEditMode || item.description"
                                    :class="[
                                      'row',
                                      'g-0',
                                      'film-list-detail__description-row',
                                      listEditMode ? 'film-list-detail__description-row--editing' : null,
                                    ]"
                                  >
                                    <div class="col g-0">
                                      <textarea
                                        v-if="listEditMode"
                                        v-model="editableItemDescriptions[item.id]"
                                        class="film-list-detail__description-textarea film-list-detail__item-description-textarea"
                                        data-testid="film-list-item-description-textarea"
                                        rows="5"
                                      />
                                      <p v-else class="film-list-detail__description-text">{{ item.description }}</p>
                                      <div
                                        v-if="listEditMode"
                                        class="film-list-detail__item-remove-row"
                                      >
                                        <button
                                          class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                          data-testid="film-list-item-remove-button"
                                          type="button"
                                          @click="removeListItemFromEditing(item.id)"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!listEditMode && !isListItemPendingRemoval(item.id)"
                              :class="[
                                'row',
                                'g-0',
                                'film-list-detail__actions-row-sm',
                                'person-credits-button-row-sm',
                                index === paginatedItems.length - 1 ? 'last-person-credits-button-row-sm' : null,
                              ]"
                              data-testid="film-list-item-action-row-sm"
                            >
                              <div class="col g-0">
                                <div class="person-buttons person-buttons--credit" data-testid="film-list-item-action-group-sm">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watched': item.film.watched }"
                                      type="button"
                                      :disabled="filmWatchPendingBySlug[item.film.slug] || item.film.watched"
                                      @click="markListItemWatched(item)"
                                    >
                                      <div class="default-btn">
                                        <i :class="listItemWatchIconClass(item)"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn left-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-plus"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn middle-btn"
                                      :class="{ 'btn--watchlisted': item.film.watchlisted }"
                                      type="button"
                                      :disabled="filmWatchlistPendingBySlug[item.film.slug]"
                                      @click="toggleListItemWatchlist(item)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn middle-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn right-btn"
                                      :class="{ 'btn--favourited': item.film.favourited }"
                                      type="button"
                                      :disabled="filmFavouritePendingBySlug[item.film.slug]"
                                      @click="toggleListItemFavourite(item)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn right-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="person-credit-rating-summary"
                                      type="button"
                                      :disabled="filmRatingPendingBySlug[item.film.slug]"
                                      @click="openRatingPicker(item)"
                                    >
                                      <i :class="listItemRatingStarClass(item)"></i>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="person-credit-rating-summary" :href="loginPath">
                                      <i class="rating-star ion-ios-star-outline"></i>
                                    </a>
                                  </template>
                                  <div class="current-credits-rating">
                                    <p>{{ listItemRatingText(item) }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!listEditMode && item.description"
                              class="row g-0 film-list-detail__description-row-sm"
                              data-testid="film-list-item-description-row-sm"
                            >
                              <div class="col g-0">
                                <p class="film-list-detail__description-text">{{ item.description }}</p>
                              </div>
                            </div>
                          </template>
                        </div>

                        <div
                          v-else-if="currentViewMode === '2'"
                          class="person-posters-grid film-list-detail__posters-grid"
                          data-testid="film-list-posters-grid"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="film-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="item in paginatedItems"
                            v-else
                            :key="item.id"
                            class="film-list-detail__poster-card"
                            data-testid="film-list-poster-card"
                          >
                            <div class="film-list-detail__poster-media">
                              <p class="film-list-detail__poster-number">{{ item.number || '–' }}</p>
                              <a class="person-poster-link" :href="item.film.url || '#'">
                                <img
                                  class="person-poster-grid-image"
                                  :src="item.film.poster_url || posterPlaceholderImage"
                                  :alt="`${item.film.title} poster`"
                                >
                                <div
                                  v-if="shouldShowPlaceholderOverlay(item.film)"
                                  class="person-poster-placeholder-overlay"
                                  data-testid="film-list-poster-placeholder-overlay"
                                >
                                  <h3 class="person-poster-placeholder-title">{{ item.film.title }}</h3>
                                  <p
                                    v-if="item.film.year"
                                    class="person-poster-placeholder-year"
                                  >
                                    {{ item.film.year }}
                                  </p>
                                </div>
                              </a>
                            </div>
                          </div>
                        </div>

                        <div
                          v-else
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list"
                          data-testid="film-list-text-only-list"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="film-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in paginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === paginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="film-list-text-row"
                          >
                            <div class="col g-0">
                              <div class="person-text-only-row film-list-detail__text-row-top">
                                <span class="film-list-detail__text-number">{{ item.number || '–' }}</span>
                                <a
                                  :href="item.film.url || '#'"
                                  class="film-list-detail__text-title-link"
                                  data-testid="film-list-text-title-link"
                                >
                                  <h3 class="person-text-only-credit-film-title">
                                    {{ truncatedFilmTitle(item.film.title) }}
                                  </h3>
                                </a>
                                <span v-if="item.film.year" class="release-year person-text-only-release-year">
                                  <span class="person-text-only-parenthesis">(</span>{{ item.film.year }}<span class="person-text-only-parenthesis">)</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <nav
                          v-if="effectiveNumPages > 1"
                          class="film-lists-pagination film-list-detail__pagination film-list-detail__pagination--bootstrap film-list-detail__pagination--with-mobile"
                          aria-label="Film list pagination"
                        >
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                              <button
                                class="page-link"
                                data-testid="film-list-prev"
                                type="button"
                                @click="goToPage(currentPage - 1)"
                              >
                                Previous
                              </button>
                            </li>
                            <li
                              v-for="pageItem in paginationItems"
                              :key="`film-list-bottom-${pageItem.key}`"
                              class="page-item"
                              :class="{ active: pageItem.page === currentPage, disabled: pageItem.ellipsis }"
                            >
                              <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                                {{ pageItem.label }}
                              </span>
                              <button
                                v-else
                                class="page-link"
                                :data-testid="`film-list-page-${pageItem.page}`"
                                type="button"
                                :aria-label="`Go to page ${pageItem.page}`"
                                @click="goToPage(pageItem.page)"
                              >
                                {{ pageItem.label }}
                              </button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage >= effectiveNumPages }">
                              <button
                                class="page-link"
                                data-testid="film-list-next"
                                type="button"
                                @click="goToPage(currentPage + 1)"
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                          <CompactMobilePagination
                            :current-page="currentPage"
                            :total-pages="effectiveNumPages"
                            test-id-prefix="film-list"
                            @page-change="goToPage"
                          />
                        </nav>

                        <div
                          v-if="listEditMode && filmListCanEdit"
                          class="film-list-detail__delete-row"
                        >
                          <button
                            class="btn-sm film-list-detail__delete-button"
                            data-testid="film-list-delete-button"
                            type="button"
                            :disabled="listDeletePending"
                            @click="deleteFilmList"
                          >
                            Delete list
                          </button>
                        </div>
                      </div>

                      <div class="col-3 g-0 filters-section" data-testid="film-list-filters-sidebar">
                        <div class="container person-filters">
                          <div class="row g-0 filters-title-row">
                            <div class="col g-0">
                              <h3>Filters:</h3>
                            </div>
                          </div>

                          <template v-for="group in filterGroups" :key="`sidebar-${group.key}`">
                            <div class="row g-0 single-filter-title-row">
                              <div class="col g-0">
                                <h3>{{ group.title }}</h3>
                              </div>
                            </div>
                            <div class="row g-0 filter-row">
                              <div class="col g-0">
                                <a
                                  v-for="option in group.options"
                                  :key="option.value"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="overviewPath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="film-list"
            :target-id="listId"
            test-id-prefix="film-list"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedRatingItem"
        class="person-compact-rating-picker-overlay"
        data-testid="film-list-rating-picker-overlay"
        @click.self="closeRatingPicker"
      >
        <div class="person-compact-rating-picker" data-testid="film-list-rating-picker">
          <button
            class="person-compact-rating-picker__close"
            data-testid="film-list-rating-picker-close"
            type="button"
            aria-label="Close rating picker"
            @click="closeRatingPicker"
          >
            <span
              class="person-compact-rating-picker__close-icon ion-close"
              aria-hidden="true"
            ></span>
          </button>

          <p class="person-compact-rating-picker__title">
            Rate {{ selectedRatingItem?.film?.title || 'film' }}
          </p>
          <div class="person-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`film-list-rating-${selectedRatingItem?.film?.slug || 'film'}-${star}`"
              class="person-compact-rating-picker__star"
              data-testid="film-list-rating-picker-star"
              type="button"
              :disabled="selectedRatingItem ? filmRatingPendingBySlug[selectedRatingItem.film.slug] : false"
              @mouseenter="previewRating(star)"
              @mouseleave="resetRatingPreview"
              @click="selectRating(star)"
            >
              <i :class="ratingPickerStarClass(star)"></i>
            </button>
          </div>
          <button
            class="person-compact-rating-picker__reset"
            data-testid="film-list-rating-picker-reset"
            type="button"
            :disabled="!selectedRatingItem || !selectedRatingItem.film.rating || filmRatingPendingBySlug[selectedRatingItem.film.slug]"
            @click="resetSelectedItemRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import ResetListNumbers from '../components/ResetListNumbers.vue'
import { useListNumberReset } from '../utils/listNumberReset'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-film-list/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()

const logoPath = '/static/spa/branding/logo.png'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const pageSize = 50
const listFavouritesEnabled = false

const mobileSearchOpen = ref(false)
const filtersModalOpen = ref(false)
const filmListLoading = ref(false)
const filmListLoaded = ref(false)
const filmListError = ref('')
const filmList = ref({
  id: null,
  title: '',
  description: '',
  owner_name: '',
  is_owner: false,
  public: true,
  film_count: 0,
  page: 1,
  page_size: pageSize,
  num_pages: 1,
  server_paginated: false,
  filter_counts: {},
  updated_at: null,
  items: [],
})
const documentTitle = computed(() => buildDocumentTitle([filmList.value.title, 'Film list']))
const selectedType = ref('all')
const selectedReleaseStatus = ref('all')
const selectedActivity = ref('all')
const sortMode = ref('number-asc')
const currentViewMode = ref('1')
const titleFilter = ref('')
const currentPage = ref(1)
const listContentStartRef = ref(null)
const ignoreFilterPageReset = ref(false)
const selectedRatingItem = ref(null)
const ratingPreview = ref(0)
const listEditMode = ref(false)
const reorderMode = ref(false)
const viewModeBeforeReordering = ref('1')
const editableListTitle = ref('')
const editableListDescription = ref('')
const editableListPublic = ref(true)
const editableItemDescriptions = ref({})
const savedEditableItems = ref([])
const pendingAddedItems = ref([])
const removedItemIds = ref([])
const addItemQuery = ref('')
const addItemSearchResults = ref([])
const addItemActiveIndex = ref(-1)
const addItemNotice = ref('')
const reorderedItemIds = ref([])
const savedReorderItemIds = ref([])
const reorderItemNumbers = ref({})
const savedReorderItemNumbers = ref({})
const reorderPageLengths = ref([])
const savedReorderPageLengths = ref([])
const reorderMoveNotice = ref({ text: '', position: '' })
const showUnsavedChangesNotice = ref(false)
const showSavedChangesNotice = ref(false)
const listDeletePending = ref(false)
let savedChangesNoticeTimeoutId = null
let reorderMoveNoticeTimeoutId = null
let addItemSearchRequestId = 0
const addItemResultRefs = []
const deleteListConfirmationMessage = "Delete this list? This will permanently delete the whole list and all its list items. This deletion can't be reverted."
const listDeletedNoticeStorageKey = 'filmsomniac:listDeletedNotice'

const filmWatchPendingBySlug = ref({})
const filmWatchlistPendingBySlug = ref({})
const filmFavouritePendingBySlug = ref({})
const filmRatingPendingBySlug = ref({})

function clearSavedChangesNoticeTimeout() {
  if (savedChangesNoticeTimeoutId !== null) {
    window.clearTimeout(savedChangesNoticeTimeoutId)
    savedChangesNoticeTimeoutId = null
  }
}

function dismissSavedChangesNotice() {
  clearSavedChangesNoticeTimeout()
  showSavedChangesNotice.value = false
}

function scheduleSavedChangesNoticeDismiss() {
  clearSavedChangesNoticeTimeout()
  savedChangesNoticeTimeoutId = window.setTimeout(() => {
    showSavedChangesNotice.value = false
    savedChangesNoticeTimeoutId = null
  }, 3000)
}

function clearReorderMoveNoticeTimeout() {
  if (reorderMoveNoticeTimeoutId !== null) {
    window.clearTimeout(reorderMoveNoticeTimeoutId)
    reorderMoveNoticeTimeoutId = null
  }
}

const authUsername = computed(() => auth.state.username || 'Account')
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const listSlugParam = computed(() => String(route.params.listSlug || ''))
const listId = computed(() => {
  const match = listSlugParam.value.match(/^(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
})
const activeSection = computed(() => (
  route.params.section === 'comments' ? 'comments' : 'overview'
))
const loginPath = computed(() => buildLoginPath(route.fullPath || overviewPath.value))
const slugifiedTitle = computed(() => slugifyText(filmList.value.title || '') || 'list')
const overviewPath = computed(() => (
  listId.value ? `/lists/film/${listId.value}-${slugifiedTitle.value}` : '/lists/film'
))
const commentsPath = computed(() => `${overviewPath.value}/comments`)
const filmTitleDisplayLimit = 80
const hasUnsavedChanges = computed(() => (
  editableListTitle.value !== (filmList.value.title || '')
    || editableListDescription.value !== (filmList.value.description || '')
    || editableListPublic.value !== Boolean(filmList.value.public)
    || !editableItemsMatchSavedItems(filmList.value.items, savedEditableItems.value)
    || removedItemIds.value.length > 0
))

const hasPendingItemDeletions = computed(() => (
  removedItemIds.value.length > 0
))

const allItems = computed(() => Array.isArray(filmList.value.items) ? filmList.value.items : [])
const filmListOwnerPath = computed(() => buildFilmsomniacPath(filmList.value.owner_name))

const filmCountLabel = computed(() => {
  const count = filmList.value.film_count || 0
  return `${count} ${count === 1 ? 'film' : 'films'}`
})

const updatedAtLabel = computed(() => formatShortDate(filmList.value.updated_at))

const sortButtons = computed(() => [
  { key: 'number-asc', label: 'Number 0-9', first: true, active: sortMode.value === 'number-asc' },
  { key: 'number-desc', label: 'Number 9-0', active: sortMode.value === 'number-desc' },
  { key: 'oldest', label: 'Oldest', active: sortMode.value === 'oldest' },
  { key: 'newest', label: 'Newest', active: sortMode.value === 'newest' },
  { key: 'title-asc', label: 'Title A-Z', active: sortMode.value === 'title-asc' },
  { key: 'title-desc', label: 'Title Z-A', active: sortMode.value === 'title-desc' },
])

const primarySortButtons = computed(() => sortButtons.value.slice(0, 4))

const secondarySortButtons = computed(() => [])

const titleSortButtons = computed(() => sortButtons.value.slice(4))

const viewModeButtons = computed(() => [
  {
    key: '1',
    label: 'Detailed view',
    first: true,
    active: currentViewMode.value === '1',
  },
  { key: '2', label: 'Poster grid view', active: currentViewMode.value === '2' },
  { key: '4', label: 'Text list view', active: currentViewMode.value === '4' },
])

function serverFilterCounts(groupKey) {
  if (!hasServerPagination.value) {
    return null
  }
  const counts = filmList.value.filter_counts?.[groupKey]
  return counts && typeof counts === 'object' ? counts : null
}

function filterCount(counts, key, fallback) {
  const value = Number.parseInt(String(counts?.[key] ?? ''), 10)
  return Number.isFinite(value) ? value : fallback
}

const currentTypeCounts = computed(() => ({
  all: filterCount(serverFilterCounts('type'), 'all', allItems.value.length),
  feature: filterCount(
    serverFilterCounts('type'),
    'feature',
    countMatches(allItems.value, (item) => item.film.type === 'feature'),
  ),
  short: filterCount(
    serverFilterCounts('type'),
    'short',
    countMatches(allItems.value, (item) => item.film.type === 'short'),
  ),
  documentary: filterCount(
    serverFilterCounts('type'),
    'documentary',
    countMatches(allItems.value, (item) => item.film.type === 'documentary'),
  ),
}))

const typeScopedItems = computed(() => {
  if (selectedType.value === 'all') {
    return allItems.value
  }
  return allItems.value.filter((item) => item.film.type === selectedType.value)
})

const currentReleaseStatusCounts = computed(() => ({
  all: filterCount(serverFilterCounts('release_status'), 'all', typeScopedItems.value.length),
  released: filterCount(
    serverFilterCounts('release_status'),
    'released',
    countMatches(typeScopedItems.value, (item) => item.film.release_status === 'released'),
  ),
  unreleased: filterCount(
    serverFilterCounts('release_status'),
    'unreleased',
    countMatches(typeScopedItems.value, (item) => item.film.release_status === 'unreleased'),
  ),
}))

const releaseStatusScopedItems = computed(() => {
  if (selectedReleaseStatus.value === 'all') {
    return typeScopedItems.value
  }
  return typeScopedItems.value.filter((item) => item.film.release_status === selectedReleaseStatus.value)
})

const activityCounts = computed(() => ({
  all: filterCount(serverFilterCounts('activity'), 'all', releaseStatusScopedItems.value.length),
  watched: filterCount(
    serverFilterCounts('activity'),
    'watched',
    countMatches(releaseStatusScopedItems.value, (item) => Boolean(item.film.watched)),
  ),
  unwatched: filterCount(
    serverFilterCounts('activity'),
    'unwatched',
    countMatches(releaseStatusScopedItems.value, (item) => !item.film.watched),
  ),
  watchlisted: filterCount(
    serverFilterCounts('activity'),
    'watchlisted',
    countMatches(releaseStatusScopedItems.value, (item) => Boolean(item.film.watchlisted)),
  ),
  rated: filterCount(
    serverFilterCounts('activity'),
    'rated',
    countMatches(releaseStatusScopedItems.value, (item) => normalizeRatingValue(item.film.rating) !== null),
  ),
  favourited: filterCount(
    serverFilterCounts('activity'),
    'favourited',
    countMatches(releaseStatusScopedItems.value, (item) => Boolean(item.film.favourited)),
  ),
}))

function buildFilterOptions(allCount, selectedValue, definitions) {
  const specificOptions = definitions
    .filter((definition) => definition.count > 0)
    .map((definition) => ({
      value: definition.value,
      label: `${definition.label} (${definition.count})`,
      active: selectedValue === definition.value,
    }))

  if (specificOptions.length === 0) {
    return [
      {
        value: 'all',
        label: `All (${allCount})`,
        active: selectedValue === 'all',
      },
    ]
  }

  if (specificOptions.length === 1) {
    return specificOptions
  }

  return [
    {
      value: 'all',
      label: `All (${allCount})`,
      active: selectedValue === 'all',
    },
    ...specificOptions,
  ]
}

const typeFilterOptions = computed(() => (
  buildFilterOptions(currentTypeCounts.value.all, selectedType.value, [
    { value: 'feature', label: 'Feature film', count: currentTypeCounts.value.feature },
    { value: 'short', label: 'Short film', count: currentTypeCounts.value.short },
    { value: 'documentary', label: 'Documentary', count: currentTypeCounts.value.documentary },
  ])
))

const releaseStatusFilterOptions = computed(() => (
  buildFilterOptions(currentReleaseStatusCounts.value.all, selectedReleaseStatus.value, [
    { value: 'released', label: 'Released', count: currentReleaseStatusCounts.value.released },
    { value: 'unreleased', label: 'Unreleased', count: currentReleaseStatusCounts.value.unreleased },
  ])
))

const activityFilterOptions = computed(() => {
  if (!isAuthenticated.value) {
    return []
  }

  return buildFilterOptions(activityCounts.value.all, selectedActivity.value, [
    { value: 'watched', label: 'Watched', count: activityCounts.value.watched },
    { value: 'unwatched', label: 'Unwatched', count: activityCounts.value.unwatched },
    { value: 'watchlisted', label: 'Watchlisted', count: activityCounts.value.watchlisted },
    { value: 'rated', label: 'Rated', count: activityCounts.value.rated },
    { value: 'favourited', label: 'Favourited', count: activityCounts.value.favourited },
  ])
})

const activityScopedItems = computed(() => {
  if (hasServerPagination.value) {
    return releaseStatusScopedItems.value
  }
  if (!isAuthenticated.value || selectedActivity.value === 'all') {
    return releaseStatusScopedItems.value
  }

  return releaseStatusScopedItems.value.filter((item) => {
    switch (selectedActivity.value) {
      case 'watched':
        return Boolean(item.film.watched)
      case 'unwatched':
        return !item.film.watched
      case 'watchlisted':
        return Boolean(item.film.watchlisted)
      case 'rated':
        return normalizeRatingValue(item.film.rating) !== null
      case 'favourited':
        return Boolean(item.film.favourited)
      default:
        return true
    }
  })
})

const normalizedTitleFilter = computed(() => titleFilter.value.trim().toLowerCase())

const filteredItems = computed(() => {
  if (normalizedTitleFilter.value.length < 2) {
    return activityScopedItems.value
  }

  return activityScopedItems.value.filter((item) => {
    const title = String(item.film.title || '').toLowerCase()
    const year = String(item.film.year || '').toLowerCase()
    return title.includes(normalizedTitleFilter.value) || year.includes(normalizedTitleFilter.value)
  })
})

const sortedItems = computed(() => {
  const items = [...filteredItems.value]

  switch (sortMode.value) {
    case 'number-desc':
      return items.sort((left, right) => numericCompare(right.number, left.number, right.film.title, left.film.title))
    case 'oldest':
      return items.sort((left, right) => numericCompare(filmYearValue(left), filmYearValue(right), left.film.title, right.film.title))
    case 'newest':
      return items.sort((left, right) => numericCompare(filmYearValue(right), filmYearValue(left), right.film.title, left.film.title))
    case 'title-asc':
      return items.sort((left, right) => compareFilmTitles(left, right))
    case 'title-desc':
      return items.sort((left, right) => compareFilmTitles(right, left))
    case 'number-asc':
    default:
      return items.sort((left, right) => numericCompare(left.number, right.number, left.film.title, right.film.title))
  }
})

const hasServerPagination = computed(() => (
  filmList.value.server_paginated
    && Number.parseInt(filmList.value.page_size, 10) === pageSize
    && Number.parseInt(filmList.value.num_pages, 10) > 0
))

const numPages = computed(() => {
  if (hasServerPagination.value) {
    return Math.max(1, Number.parseInt(filmList.value.num_pages, 10) || 1)
  }
  return Math.max(1, Math.ceil(sortedItems.value.length / pageSize))
})

const paginatedItems = computed(() => {
  if (hasServerPagination.value) {
    return sortedItems.value
  }
  const startIndex = (currentPage.value - 1) * pageSize
  return sortedItems.value.slice(startIndex, startIndex + pageSize)
})

const reorderOrderedItems = computed(() => {
  if (!reorderMode.value) {
    return sortedItems.value
  }

  const itemsById = new Map(sortedItems.value.map((item) => [item.id, item]))
  const orderedIds = reorderedItemIds.value.filter((id) => itemsById.has(id))
  const missingIds = sortedItems.value
    .map((item) => item.id)
    .filter((id) => !orderedIds.includes(id))

  return [...orderedIds, ...missingIds]
    .map((id) => itemsById.get(id))
    .filter(Boolean)
})

const reorderNumPages = computed(() => (
  Math.max(1, reorderPageLengths.value.length || 1)
))

const effectiveNumPages = computed(() => (
  reorderMode.value ? reorderNumPages.value : numPages.value
))
const paginationItems = computed(() => compactPaginationItems(currentPage.value, effectiveNumPages.value))

const reorderPaginatedItems = computed(() => {
  const startIndex = reorderPageStartIndex(currentPage.value)
  const currentLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  return reorderOrderedItems.value.slice(startIndex, startIndex + currentLength)
})

const hasReorderChanges = computed(() => {
  if (!reorderMode.value) {
    return false
  }

  if (reorderedItemIds.value.length !== savedReorderItemIds.value.length) {
    return true
  }

  if (reorderPageLengths.value.length !== savedReorderPageLengths.value.length) {
    return true
  }

  if (reorderPageLengths.value.some((length, index) => length !== savedReorderPageLengths.value[index])) {
    return true
  }

  if (reorderedItemIds.value.some((id, index) => id !== savedReorderItemIds.value[index])) {
    return true
  }

  return reorderedItemIds.value.some((id) => (
    reorderItemNumbers.value[id] !== savedReorderItemNumbers.value[id]
  ))
})

const noItemsMessage = computed(() => (
  activityScopedItems.value.length === 0
    ? 'No films on this list.'
    : 'No films found.'
))

const showNoResults = computed(() => (
  activityScopedItems.value.length > 0 && filteredItems.value.length === 0
))

const filterGroups = computed(() => {
  const groups = [
    {
      key: 'type',
      title: 'Type:',
      options: typeFilterOptions.value,
    },
    {
      key: 'release-status',
      title: 'Release status',
      options: releaseStatusFilterOptions.value,
    },
  ]

  if (isAuthenticated.value) {
    groups.push({
      key: 'activity',
      title: 'Your activity',
      options: activityFilterOptions.value,
    })
  }

  return groups
})
const filterModalRows = computed(() => {
  const groups = filterGroups.value
  const rows = []

  for (let index = 0; index < groups.length; index += 2) {
    const rowGroups = groups.slice(index, index + 2)
    rows.push({
      key: rowGroups.map((group) => group.key).join('-'),
      groups: rowGroups,
    })
  }

  return rows
})
const filterButtonLabel = computed(() => {
  return 'Filters'
})

function pageFromRoute() {
  const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const page = Number.parseInt(String(rawPage || ''), 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

function normalizedPage(pageNumber) {
  return Math.min(Math.max(pageNumber, 1), effectiveNumPages.value)
}

function queryForPage(pageNumber) {
  const query = { ...route.query }
  if (pageNumber > 1) {
    query.page = String(pageNumber)
  } else {
    delete query.page
  }
  return query
}

function syncPageRoute(pageNumber, { replace = true } = {}) {
  const location = { path: route.path, query: queryForPage(pageNumber) }
  return replace ? router.replace(location) : router.push(location)
}

function resetCurrentPage() {
  currentPage.value = 1
  void syncPageRoute(1)
}

async function scrollToListContentStart() {
  await nextTick()
  if (typeof listContentStartRef.value?.scrollIntoView === 'function') {
    listContentStartRef.value.scrollIntoView({ behavior: 'auto', block: 'start' })
  }
}

watch([selectedType, selectedReleaseStatus, selectedActivity], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!filmListLoaded.value) {
    return
  }
  resetCurrentPage()
  if (hasServerPagination.value && !listEditMode.value && !reorderMode.value) {
    void loadFilmListDetail(listId.value, { page: 1, preserveFilters: true })
  }
})

watch([sortMode, titleFilter], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!filmListLoaded.value) {
    return
  }
  if (!hasServerPagination.value) {
    resetCurrentPage()
  }
})

watch(typeFilterOptions, (options) => {
  if (!options.some((option) => option.value === selectedType.value)) {
    selectedType.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch(releaseStatusFilterOptions, (options) => {
  if (!options.some((option) => option.value === selectedReleaseStatus.value)) {
    selectedReleaseStatus.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch(activityFilterOptions, (options) => {
  if (!isAuthenticated.value) {
    selectedActivity.value = 'all'
    return
  }
  if (!options.some((option) => option.value === selectedActivity.value)) {
    selectedActivity.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch([editableListTitle, editableListDescription, editableItemDescriptions, savedEditableItems, removedItemIds], () => {
  if (listEditMode.value && hasUnsavedChanges.value) {
    dismissSavedChangesNotice()
  }
}, { deep: true })

onBeforeUnmount(() => {
  clearSavedChangesNoticeTimeout()
  clearReorderMoveNoticeTimeout()
})

watch(numPages, (value) => {
  if (currentPage.value > value) {
    currentPage.value = value
    void syncPageRoute(value)
  }
})

watch(() => route.query.page, async () => {
  currentPage.value = normalizedPage(pageFromRoute())
  if (
    listId.value
      && filmListLoaded.value
      && hasServerPagination.value
      && !reorderMode.value
  ) {
    await loadFilmListDetail(listId.value, {
      preserveFilters: true,
      preserveEditMode: listEditMode.value,
    })
  }
})

watch([listId, isAuthenticated], async ([currentListId]) => {
  if (!currentListId) {
    filmList.value = emptyFilmList()
    filmListLoaded.value = true
    filmListError.value = 'This list could not be found.'
    return
  }
  await loadFilmListDetail(currentListId)
}, { immediate: true })

watch(documentTitle, setDocumentTitle, { immediate: true })

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function buildJsonAuthHeaders() {
  return {
    ...buildAuthHeaders(),
    'Content-Type': 'application/json',
  }
}

function emptyFilmList() {
  return {
    id: null,
    title: '',
    description: '',
    owner_name: '',
    is_owner: false,
    film_count: 0,
    page: 1,
    page_size: pageSize,
    num_pages: 1,
    server_paginated: false,
    filter_counts: {},
    updated_at: null,
    items: [],
  }
}

function buildEditableItemDescriptions(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item) => [item.id, item.description || '']),
  )
}

function renumberFilmListItems(items) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    ...item,
    number: index + 1,
    film: item?.film ? { ...item.film } : item.film,
  }))
}

function resetRemovedItemIds() {
  removedItemIds.value = []
}

function isListItemPendingRemoval(itemId) {
  return removedItemIds.value.includes(itemId)
}

function cloneFilmListItems(items) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    film: item?.film ? { ...item.film } : item.film,
  }))
}

function editableItemsMatchSavedItems(currentItems, savedItems) {
  if (currentItems.length !== savedItems.length) {
    return false
  }

  return currentItems.every((item, index) => {
    const savedItem = savedItems[index]
    return savedItem
      && item.id === savedItem.id
      && (editableItemDescriptions.value[item.id] ?? '') === (savedItem.description || '')
  })
}

function buildReorderedItemIds(items) {
  return (Array.isArray(items) ? items : []).map((item) => item.id)
}

function buildReorderItemNumbers(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item, index) => [
      item.id,
      Number.isFinite(Number.parseInt(item.number, 10)) ? Number.parseInt(item.number, 10) : index + 1,
    ]),
  )
}

function reorderItemNumber(item) {
  if (!item) {
    return 0
  }
  return reorderItemNumbers.value[item.id] ?? item.number ?? 0
}

function resetAddItemSearch() {
  addItemSearchRequestId += 1
  addItemQuery.value = ''
  addItemSearchResults.value = []
  addItemActiveIndex.value = -1
  addItemResultRefs.length = 0
}

function resetPendingAddedItems() {
  pendingAddedItems.value = []
}

function isFilmAlreadyOnList(filmId) {
  return filmList.value.items.some((item) => item?.film?.id === filmId)
}

function isFilmPendingAdd(filmId) {
  return pendingAddedItems.value.some((item) => item?.film?.id === filmId)
}

function isFilmAddItemDisabled(filmId) {
  return isFilmAlreadyOnList(filmId) || isFilmPendingAdd(filmId)
}

async function handleAddItemSearchInput() {
  if (!listEditMode.value || !filmListCanEdit.value || !listId.value) {
    resetAddItemSearch()
    return
  }

  const query = addItemQuery.value.trim()
  const requestId = ++addItemSearchRequestId

  if (query.length < 2) {
    addItemSearchResults.value = []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
    return
  }

  try {
    const response = await fetch(`/api/v1/films/lists/${listId.value}/search?q=${encodeURIComponent(query)}`, {
      headers: buildAuthHeaders(),
    })

    if (requestId !== addItemSearchRequestId) {
      return
    }

    if (!response.ok) {
      addItemSearchResults.value = []
      return
    }

    const data = await response.json()
    addItemSearchResults.value = Array.isArray(data.results) ? data.results : []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
  } catch {
    if (requestId === addItemSearchRequestId) {
      addItemSearchResults.value = []
      addItemActiveIndex.value = -1
      addItemResultRefs.length = 0
    }
  }
}

function setAddItemResultRef(element, index) {
  if (!element) {
    return
  }
  addItemResultRefs[index] = element
}

async function scrollActiveAddItemResultIntoView() {
  await nextTick()
  const activeElement = addItemResultRefs[addItemActiveIndex.value]
  if (typeof activeElement?.scrollIntoView === 'function') {
    activeElement.scrollIntoView({ block: 'nearest' })
  }
}

function setAddItemActiveIndex(index) {
  addItemActiveIndex.value = index
  scrollActiveAddItemResultIntoView()
}

function isAddItemSearchResultSelectable(result) {
  return Boolean(result?.id) && !isFilmAddItemDisabled(result.id)
}

function setSelectableAddItemActiveIndex(result, index) {
  if (isAddItemSearchResultSelectable(result)) {
    setAddItemActiveIndex(index)
  }
}

function getNextSelectableAddItemIndex(direction) {
  const results = addItemSearchResults.value
  if (results.length === 0) {
    return -1
  }

  const startIndex = addItemActiveIndex.value < 0
    ? (direction > 0 ? -1 : results.length)
    : addItemActiveIndex.value

  for (let offset = 1; offset <= results.length; offset += 1) {
    const index = (startIndex + (direction * offset) + results.length) % results.length
    if (isAddItemSearchResultSelectable(results[index])) {
      return index
    }
  }

  return -1
}

function highlightNextAddItemResult() {
  const nextIndex = getNextSelectableAddItemIndex(1)
  if (nextIndex >= 0) {
    setAddItemActiveIndex(nextIndex)
  }
}

function highlightPreviousAddItemResult() {
  const previousIndex = getNextSelectableAddItemIndex(-1)
  if (previousIndex >= 0) {
    setAddItemActiveIndex(previousIndex)
  }
}

function handleAddItemEnter() {
  if (addItemActiveIndex.value < 0) {
    return
  }
  addSearchResult(addItemSearchResults.value[addItemActiveIndex.value])
}

async function addSearchResult(result) {
  if (!result?.id || isFilmAddItemDisabled(result.id)) {
    return
  }

  pendingAddedItems.value = [
    ...pendingAddedItems.value,
    {
      number: filmList.value.items.length + 1,
      description: '',
      film: {
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        url: result.url || '#',
        year: result.year || '',
        poster_url: result.poster_url || null,
        type: result.type || 'feature',
        release_status: result.release_status || 'released',
        watched: false,
        watchlisted: false,
        favourited: false,
        rating: null,
      },
    },
  ]
  const nextNumber = filmList.value.items.length + 1

  try {
    const response = await fetch(`/api/v1/films/lists/${listId.value}/`, {
      method: 'PATCH',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({
        items: [
          ...filmList.value.items.map((item) => ({
            id: item.id,
            description: item.description || '',
          })),
          { film_id: result.id, description: '', number: nextNumber },
        ],
      }),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    const fallbackNewItem = {
      id: `new-${result.id}`,
      number: nextNumber,
      description: '',
      added_at: null,
      updated_at: null,
      film: {
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        url: result.url || '#',
        year: result.year || '',
        poster_url: result.poster_url || null,
        type: result.type || 'feature',
        release_status: result.release_status || 'released',
        watched: false,
        watchlisted: false,
        favourited: false,
        rating: null,
      },
    }
    const nextItems = Array.isArray(data.items) ? data.items : [...filmList.value.items, fallbackNewItem]

    filmList.value = {
      ...filmList.value,
      ...data,
      title: data.title ?? filmList.value.title,
      description: data.description ?? filmList.value.description,
      film_count: data.film_count ?? nextItems.length,
      items: nextItems,
    }

    const nextEditableItemDescriptions = { ...editableItemDescriptions.value }
    nextItems.forEach((item) => {
      if (!(item.id in nextEditableItemDescriptions)) {
        nextEditableItemDescriptions[item.id] = item.description || ''
      }
    })
    editableItemDescriptions.value = nextEditableItemDescriptions
    savedEditableItems.value = cloneFilmListItems(filmList.value.items)
    addItemNotice.value = `'${result.title || 'Item'}' added to the list as #${nextNumber}.`
  } finally {
    pendingAddedItems.value = pendingAddedItems.value.filter((item) => item?.film?.id !== result.id)
    resetAddItemSearch()
  }
}

function removeListItemFromEditing(itemId) {
  if (isListItemPendingRemoval(itemId)) {
    return
  }
  removedItemIds.value = [...removedItemIds.value, itemId]
  dismissSavedChangesNotice()
}

function undoListItemRemoval(itemId) {
  removedItemIds.value = removedItemIds.value.filter((id) => id !== itemId)
  dismissSavedChangesNotice()
}

function buildReorderPageLengths(itemCount) {
  const lengths = []
  let remaining = Math.max(0, itemCount)

  while (remaining > 0) {
    const nextLength = Math.min(pageSize, remaining)
    lengths.push(nextLength)
    remaining -= nextLength
  }

  return lengths.length > 0 ? lengths : [0]
}

function reorderPageStartIndex(pageNumber) {
  const boundedPageNumber = Math.max(1, pageNumber)
  return reorderPageLengths.value
    .slice(0, boundedPageNumber - 1)
    .reduce((sum, length) => sum + length, 0)
}

function reorderDisplayNumber(localIndex) {
  return reorderItemNumber(reorderPaginatedItems.value[localIndex])
}

async function startListEditing() {
  if (!filmListCanEdit.value) {
    return
  }

  if (listEditMode.value) {
    if (!hasUnsavedChanges.value) {
      editableListTitle.value = filmList.value.title || ''
      editableListDescription.value = filmList.value.description || ''
      editableListPublic.value = Boolean(filmList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(filmList.value.items)
      savedEditableItems.value = cloneFilmListItems(filmList.value.items)
      resetPendingAddedItems()
      resetRemovedItemIds()
      resetFiltersForNormalListMode()
      listEditMode.value = false
      showUnsavedChangesNotice.value = false
      addItemNotice.value = ''
      resetAddItemSearch()
      dismissSavedChangesNotice()
    } else {
      showUnsavedChangesNotice.value = true
      dismissSavedChangesNotice()
    }
    return
  }

  await loadAllReorderItems()
  editableListTitle.value = filmList.value.title || ''
  editableListDescription.value = filmList.value.description || ''
  editableListPublic.value = Boolean(filmList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(filmList.value.items)
  savedEditableItems.value = cloneFilmListItems(filmList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  currentViewMode.value = '1'
  reorderMode.value = false
  listEditMode.value = true
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

const filmListCanEdit = computed(() => Boolean(
  filmList.value.can_edit ?? filmList.value.is_owner,
))

const { resetNumbersPending, resetNumbersNotice, resetNumbers } = useListNumberReset(
  filmList,
  buildAuthHeaders,
  () => loadFilmListDetail(listId.value, { page: currentPage.value, preserveFilters: true }),
)

async function startReordering() {
  if (!filmListCanEdit.value) {
    return
  }

  titleFilter.value = ''
  await loadAllReorderItems()
  viewModeBeforeReordering.value = currentViewMode.value
  reorderedItemIds.value = buildReorderedItemIds(sortedItems.value)
  savedReorderItemIds.value = buildReorderedItemIds(sortedItems.value)
  reorderItemNumbers.value = buildReorderItemNumbers(sortedItems.value)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(sortedItems.value.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
  reorderMode.value = true
  resetCurrentPage()
  currentViewMode.value = '4'
  listEditMode.value = false
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

async function loadAllReorderItems() {
  if (!hasServerPagination.value || numPages.value <= 1 || !listId.value) {
    return
  }

  const pageCount = numPages.value
  const pageItems = new Map()

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    if (pageNumber === currentPage.value) {
      pageItems.set(pageNumber, filmList.value.items)
      continue
    }

    const response = await fetch(`/api/v1/films/lists/${listId.value}/${filmListDetailQuery(pageNumber)}`, {
      headers: buildAuthHeaders(),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    pageItems.set(pageNumber, Array.isArray(data.items) ? data.items : [])
  }

  const combinedItems = []
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    combinedItems.push(...(pageItems.get(pageNumber) || []))
  }

  filmList.value = {
    ...filmList.value,
    film_count: combinedItems.length,
    items: combinedItems,
    page: 1,
    num_pages: Math.max(1, Math.ceil(combinedItems.length / pageSize)),
    server_paginated: false,
  }
}

function finishReordering() {
  if (!reorderMode.value) {
    return
  }

  if (!hasReorderChanges.value) {
    currentViewMode.value = viewModeBeforeReordering.value || '1'
    resetCurrentPage()
    reorderMode.value = false
    showUnsavedChangesNotice.value = false
    dismissSavedChangesNotice()
    return
  }

  showUnsavedChangesNotice.value = true

  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
  currentViewMode.value = viewModeBeforeReordering.value || '1'
  resetCurrentPage()
  reorderMode.value = false
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

function resetReorderChanges() {
  reorderedItemIds.value = [...savedReorderItemIds.value]
  reorderItemNumbers.value = { ...savedReorderItemNumbers.value }
  reorderPageLengths.value = [...savedReorderPageLengths.value]
  clearReorderMoveNotice()
}

function discardReorderChanges() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
}

async function saveReordering() {
  if (!filmListCanEdit.value || !listId.value) {
    return
  }

  const reorderedItems = reorderOrderedItems.value
    .map((item) => ({
      id: item.id,
      description: item.description || '',
      number: reorderItemNumber(item),
    }))
    .filter((item) => item.number !== savedReorderItemNumbers.value[item.id])

  const response = await fetch(`/api/v1/films/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      description: filmList.value.description || '',
      removed_item_ids: [],
      items: reorderedItems,
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const reorderedItemsById = new Map(
    reorderedItems.map((item) => [item.id, item]),
  )
  const nextItems = reorderOrderedItems.value.map((item) => ({
    ...item,
    description: reorderedItemsById.get(item.id)?.description ?? item.description ?? '',
    number: reorderItemNumber(item),
  }))

  filmList.value = {
    ...filmList.value,
    ...data,
    description: data.description ?? '',
    items: nextItems,
  }
  reorderedItemIds.value = buildReorderedItemIds(filmList.value.items)
  savedReorderItemIds.value = [...reorderedItemIds.value]
  reorderItemNumbers.value = buildReorderItemNumbers(filmList.value.items)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(filmList.value.items.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
}

function reorderItemIndex(itemId) {
  return reorderOrderedItems.value.findIndex((item) => item.id === itemId)
}

function isFirstReorderItem(itemId) {
  return reorderItemIndex(itemId) === 0
}

function isLastReorderItem(itemId) {
  const itemIndex = reorderItemIndex(itemId)
  return itemIndex === reorderOrderedItems.value.length - 1
}

function clearReorderMoveNotice() {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text: '', position: '' }
}

function showReorderMoveNotice(text, position) {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text, position }
  reorderMoveNoticeTimeoutId = window.setTimeout(() => {
    reorderMoveNotice.value = { text: '', position: '' }
    reorderMoveNoticeTimeoutId = null
  }, 5000)
}

function filmReorderItemTitle(item) {
  return item?.film?.title || 'List item'
}

function moveReorderItem(itemId, direction) {
  if (!reorderMode.value || !direction) {
    return
  }

  const nextIds = [...reorderedItemIds.value]
  const currentIndex = nextIds.indexOf(itemId)
  const targetIndex = currentIndex + direction

  if (currentIndex === -1 || targetIndex < 0 || targetIndex >= nextIds.length) {
    return
  }

  const currentPageStartIndex = reorderPageStartIndex(currentPage.value)
  const currentPageLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  const currentPageEndIndex = currentPageStartIndex + currentPageLength - 1
  const movedItem = reorderOrderedItems.value[currentIndex]
  const movedTitle = filmReorderItemTitle(movedItem)

  const targetId = nextIds[targetIndex]
  const targetItem = reorderOrderedItems.value[targetIndex]
  nextIds[targetIndex] = itemId
  nextIds[currentIndex] = targetId

  reorderedItemIds.value = nextIds
  reorderItemNumbers.value = {
    ...reorderItemNumbers.value,
    [itemId]: reorderItemNumber(targetItem),
    [targetId]: reorderItemNumber(movedItem),
  }

  if (direction > 0 && currentIndex === currentPageEndIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to next page to place #${targetIndex + 1}.`, 'below')
  } else if (direction < 0 && currentIndex === currentPageStartIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to previous page to place #${targetIndex + 1}.`, 'above')
  } else {
    clearReorderMoveNotice()
  }
}
function discardListEditing() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  filmList.value = {
    ...filmList.value,
    film_count: savedEditableItems.value.length,
    items: cloneFilmListItems(savedEditableItems.value),
  }
  editableListDescription.value = filmList.value.description || ''
  editableListTitle.value = filmList.value.title || ''
  editableListPublic.value = Boolean(filmList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(filmList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

async function saveListEditing() {
  if (!filmListCanEdit.value || !listId.value) {
    return
  }

  if (hasPendingItemDeletions.value) {
    const shouldDelete = window.confirm('There are items to be deleted. Save changes and delete them?')
    if (!shouldDelete) {
      return
    }
  }

  const response = await fetch(`/api/v1/films/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: editableListTitle.value,
      description: editableListDescription.value,
      public: editableListPublic.value,
      removed_item_ids: removedItemIds.value,
      items: filmList.value.items
        .filter((item) => !isListItemPendingRemoval(item.id))
        .map((item) => ({
          id: item.id,
          description: editableItemDescriptions.value[item.id] ?? '',
        })),
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const nextItems = Array.isArray(data.items)
    ? data.items
    : filmList.value.items.map((item) => ({
        ...item,
        description: editableItemDescriptions.value[item.id] ?? '',
      }))

  filmList.value = {
    ...filmList.value,
    ...data,
    title: data.title ?? '',
    description: data.description ?? '',
    film_count: data.film_count ?? nextItems.length,
    items: nextItems,
  }
  editableListTitle.value = filmList.value.title || ''
  editableListDescription.value = filmList.value.description || ''
  editableListPublic.value = Boolean(filmList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(filmList.value.items)
  savedEditableItems.value = cloneFilmListItems(filmList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  showSavedChangesNotice.value = true
  scheduleSavedChangesNoticeDismiss()
}

async function deleteFilmList() {
  if (!filmListCanEdit.value || !listId.value || listDeletePending.value) {
    return
  }

  const shouldDelete = window.confirm(deleteListConfirmationMessage)
  if (!shouldDelete) {
    return
  }

  listDeletePending.value = true
  try {
    const response = await fetch(`/api/v1/films/lists/${listId.value}/`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })

    if (response.ok) {
      storeListDeletedNotice('Film', filmList.value.title)
      await router.push('/home/lists')
    }
  } finally {
    listDeletePending.value = false
  }
}

function storeListDeletedNotice(listMediumType, listTitle) {
  try {
    window.sessionStorage.setItem(
      listDeletedNoticeStorageKey,
      JSON.stringify({
        message: `${listMediumType} list '${String(listTitle || '').trim() || 'Untitled'}' was deleted.`,
      }),
    )
  } catch {
    // The redirect still works if browser storage is unavailable.
  }
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function truncatedFilmTitle(value) {
  const normalized = String(value || '').trim()

  if (normalized.length <= filmTitleDisplayLimit) {
    return normalized
  }

  const truncated = normalized.slice(0, filmTitleDisplayLimit).trim()
  const lastSpaceIndex = truncated.lastIndexOf(' ')

  if (lastSpaceIndex <= 0) {
    return `${truncated}...`
  }

  return `${truncated.slice(0, lastSpaceIndex).trim()}...`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function formatShortDate(value) {
  if (!value) {
    return ''
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed)
}

function normalizeRatingValue(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const normalized = Number.parseInt(String(value), 10)
  if (Number.isNaN(normalized) || normalized < 1 || normalized > 10) {
    return null
  }
  return normalized
}

function isPlaceholderPoster(film) {
  if (!film?.poster_url) {
    return true
  }
  return String(film.poster_url).endsWith('placeholder-film-camera.svg') ||
    String(film.poster_url).endsWith('placeholder-film.svg')
}

function shouldShowPlaceholderOverlay(film) {
  return isPlaceholderPoster(film)
}

function countMatches(items, matcher) {
  return items.reduce((total, item) => (matcher(item) ? total + 1 : total), 0)
}

function compareStrings(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' })
}

function filmTitleSortKey(item) {
  return String(item?.film?.title || '').replace(/^the\s+/i, '')
}

function compareFilmTitles(left, right) {
  return (
    compareStrings(filmTitleSortKey(left), filmTitleSortKey(right))
    || compareStrings(left?.film?.title, right?.film?.title)
  )
}

function numericCompare(leftValue, rightValue, leftTitle, rightTitle) {
  const leftNumber = Number.parseInt(String(leftValue || 0), 10) || 0
  const rightNumber = Number.parseInt(String(rightValue || 0), 10) || 0
  if (leftNumber !== rightNumber) {
    return leftNumber - rightNumber
  }
  return compareStrings(leftTitle, rightTitle)
}

function filmYearValue(item) {
  return Number.parseInt(String(item?.film?.year || ''), 10) || 0
}

function defaultTypeValue(items) {
  const counts = serverFilterCounts('type')
  const featureCount = filterCount(counts, 'feature', countMatches(items, (item) => item.film.type === 'feature'))
  if (featureCount > 0) {
    return 'feature'
  }
  const shortCount = filterCount(counts, 'short', countMatches(items, (item) => item.film.type === 'short'))
  if (shortCount > 0) {
    return 'short'
  }
  const documentaryCount = filterCount(
    counts,
    'documentary',
    countMatches(items, (item) => item.film.type === 'documentary'),
  )
  if (documentaryCount > 0) {
    return 'documentary'
  }
  return 'all'
}

function defaultReleaseStatusValue(items, typeValue) {
  const counts = serverFilterCounts('release_status')
  const releasedCount = filterCount(counts, 'released', null)
  const unreleasedCount = filterCount(counts, 'unreleased', null)
  if (releasedCount !== null || unreleasedCount !== null) {
    if ((releasedCount || 0) > 0) {
      return 'released'
    }
    if ((unreleasedCount || 0) > 0) {
      return 'unreleased'
    }
    return 'all'
  }

  const scopedItems = typeValue === 'all'
    ? items
    : items.filter((item) => item.film.type === typeValue)
  if (countMatches(scopedItems, (item) => item.film.release_status === 'released') > 0) {
    return 'released'
  }
  if (countMatches(scopedItems, (item) => item.film.release_status === 'unreleased') > 0) {
    return 'unreleased'
  }
  return 'all'
}

function initializeFilters(items) {
  ignoreFilterPageReset.value = true
  void nextTick(() => {
    ignoreFilterPageReset.value = false
  })
  const nextType = defaultTypeValue(items)
  selectedType.value = nextType
  selectedReleaseStatus.value = defaultReleaseStatusValue(items, nextType)
  selectedActivity.value = 'all'
  sortMode.value = 'number-asc'
  currentViewMode.value = '1'
  viewModeBeforeReordering.value = '1'
  titleFilter.value = ''
  currentPage.value = normalizedPage(pageFromRoute())
  filtersModalOpen.value = false
}

function resetFiltersForNormalListMode() {
  selectedType.value = 'all'
  selectedReleaseStatus.value = 'all'
  selectedActivity.value = 'all'
  titleFilter.value = ''
  resetCurrentPage()
  filtersModalOpen.value = false
}

function filmListDetailQuery(pageNumber, { sort = sortMode.value } = {}) {
  const params = new URLSearchParams()
  if (pageNumber > 1) {
    params.set('page', String(pageNumber))
  }
  if (selectedType.value !== 'all') {
    params.set('type', selectedType.value)
  }
  if (selectedReleaseStatus.value !== 'all') {
    params.set('release_status', selectedReleaseStatus.value)
  }
  if (isAuthenticated.value && selectedActivity.value !== 'all') {
    params.set('activity', selectedActivity.value)
  }
  if (sort !== 'number-asc') {
    params.set('sort', sort)
  }
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

async function loadFilmListDetail(currentListId, {
  page = null,
  preserveFilters = false,
  preserveEditMode = false,
  sort = sortMode.value,
} = {}) {
  const confirmPageView = capturePageView()
  const shouldPreserveEditMode = preserveEditMode && listEditMode.value
  filmListLoading.value = true
  filmListLoaded.value = false
  filmListError.value = ''
  selectedRatingItem.value = null
  ratingPreview.value = 0
  if (!shouldPreserveEditMode) {
    listEditMode.value = false
  }
  reorderMode.value = false
  editableListTitle.value = ''
  editableListDescription.value = ''
  editableListPublic.value = true
  editableItemDescriptions.value = {}
  savedEditableItems.value = []
  resetPendingAddedItems()
  resetRemovedItemIds()
  addItemNotice.value = ''
  resetAddItemSearch()
  reorderedItemIds.value = []
  savedReorderItemIds.value = []
  reorderItemNumbers.value = {}
  savedReorderItemNumbers.value = {}
  reorderPageLengths.value = []
  savedReorderPageLengths.value = []
  clearReorderMoveNotice()
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()

  try {
    const requestedPage = page ?? pageFromRoute()
    const detailUrl = `/api/v1/films/lists/${currentListId}/${filmListDetailQuery(requestedPage, { sort })}`
    const response = await fetch(detailUrl, {
      headers: buildAuthHeaders(),
    })

    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      filmListError.value = 'This list could not be found.'
      filmList.value = emptyFilmList()
      filmListLoaded.value = true
      return
    }

    if (response.status === 404) {
      filmListError.value = 'This list could not be found.'
      filmList.value = emptyFilmList()
      filmListLoaded.value = true
      return
    }

    if (!response.ok) {
      filmListError.value = 'The list could not be loaded.'
      filmList.value = emptyFilmList()
      filmListLoaded.value = true
      return
    }

    const data = await response.json()
    if (data.redirect_url) {
      window.location.assign(data.redirect_url)
      return
    }
    confirmPageView()
    filmList.value = {
      ...emptyFilmList(),
      ...data,
      server_paginated: Object.prototype.hasOwnProperty.call(data, 'num_pages'),
      items: Array.isArray(data.items) ? data.items : [],
    }
    currentPage.value = normalizedPage(Number.parseInt(filmList.value.page, 10) || requestedPage)
    savedEditableItems.value = cloneFilmListItems(filmList.value.items)
    if (shouldPreserveEditMode) {
      editableListTitle.value = filmList.value.title || ''
      editableListDescription.value = filmList.value.description || ''
      editableListPublic.value = Boolean(filmList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(filmList.value.items)
      listEditMode.value = true
    }
    if (!preserveFilters) {
      initializeFilters(filmList.value.items)
    }
    filmListLoaded.value = true
  } catch {
    filmListError.value = 'The list could not be loaded.'
    filmList.value = emptyFilmList()
    filmListLoaded.value = true
  } finally {
    filmListLoading.value = false
  }
}

async function setSortMode(mode) {
  if (hasServerPagination.value && listId.value) {
    resetCurrentPage()
    await loadFilmListDetail(listId.value, { page: 1, preserveFilters: true, sort: mode })
  }
  sortMode.value = mode
}

function setViewMode(mode) {
  currentViewMode.value = mode
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

function applyFilter(groupKey, value) {
  switch (groupKey) {
    case 'type':
      selectedType.value = value
      break
    case 'release-status':
      selectedReleaseStatus.value = value
      break
    case 'activity':
      selectedActivity.value = value
      break
    default:
      break
  }

}

async function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > effectiveNumPages.value) {
    return
  }
  if (listEditMode.value && hasUnsavedChanges.value) {
    showUnsavedChangesNotice.value = true
    dismissSavedChangesNotice()
    return
  }
  clearReorderMoveNotice()
  await syncPageRoute(pageNumber, { replace: false })
  currentPage.value = pageNumber
  await scrollToListContentStart()
}

function listItemWatchIconClass(item) {
  return item.film.watched ? 'ion-checkmark' : 'ion-plus'
}

function listItemRatingStarClass(item) {
  return [
    'rating-star',
    normalizeRatingValue(item.film.rating) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function listItemRatingText(item) {
  const normalized = normalizeRatingValue(item.film.rating)
  return normalized ? `${normalized} / 10` : '/ 10'
}

function openRatingPicker(item) {
  if (!isAuthenticated.value || !item?.film?.slug) {
    return
  }
  selectedRatingItem.value = item
  ratingPreview.value = 0
}

function closeRatingPicker() {
  selectedRatingItem.value = null
  ratingPreview.value = 0
}

function previewRating(star) {
  ratingPreview.value = star
}

function resetRatingPreview() {
  ratingPreview.value = 0
}

function ratingPickerStarClass(star) {
  const currentRating = normalizeRatingValue(selectedRatingItem.value?.film?.rating) || 0
  const visibleRating = ratingPreview.value || currentRating
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

async function markListItemWatched(item) {
  const slug = item?.film?.slug
  if (
    !slug
    || !isAuthenticated.value
    || item.film.watched
    || filmWatchPendingBySlug.value[slug]
  ) {
    return
  }

  filmWatchPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/films/${slug}/watch`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    item.film.watched = true
  } catch {
    // Leave the local watched state unchanged on request failure.
  } finally {
    filmWatchPendingBySlug.value[slug] = false
  }
}

async function toggleListItemWatchlist(item) {
  const slug = item?.film?.slug
  if (!slug || !isAuthenticated.value || filmWatchlistPendingBySlug.value[slug]) {
    return
  }

  filmWatchlistPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/films/${slug}/watchlist`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.film.watchlisted = Boolean(data.watchlisted)
  } catch {
    // Leave the local watchlist state unchanged on request failure.
  } finally {
    filmWatchlistPendingBySlug.value[slug] = false
  }
}

async function toggleListItemFavourite(item) {
  const slug = item?.film?.slug
  if (!slug || !isAuthenticated.value || filmFavouritePendingBySlug.value[slug]) {
    return
  }

  filmFavouritePendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/films/${slug}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.film.favourited = Boolean(data.favourited)
    if (data.favourited && !item.film.watched) {
      await markListItemWatched(item)
    }
  } catch {
    // Leave the local favourite state unchanged on request failure.
  } finally {
    filmFavouritePendingBySlug.value[slug] = false
  }
}

async function selectRating(ratingValue) {
  const item = selectedRatingItem.value
  const slug = item?.film?.slug
  if (!slug || !isAuthenticated.value || filmRatingPendingBySlug.value[slug]) {
    return
  }

  filmRatingPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/films/${slug}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.film.rating = normalizeRatingValue(data.rating)
    if (item.film.rating && !item.film.watched) {
      await markListItemWatched(item)
    }
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    filmRatingPendingBySlug.value[slug] = false
  }
}

async function resetSelectedItemRating() {
  const item = selectedRatingItem.value
  const slug = item?.film?.slug
  if (
    !slug
    || !isAuthenticated.value
    || !normalizeRatingValue(item?.film?.rating)
    || filmRatingPendingBySlug.value[slug]
  ) {
    return
  }

  filmRatingPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/films/${slug}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    item.film.rating = null
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    filmRatingPendingBySlug.value[slug] = false
  }
}

useMetaDescription(() => buildMetaDescription(
  'film-list',
  filmListLoaded.value && !filmListError.value ? { title: filmList.value.title } : {},
))
</script>
